import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import './Signals.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faHeart,
  faUserCircle,
  faShoppingBag,
  faPaperPlane,
  faBookmark,
  faFilter,
  faNewspaper,
  faArrowUp,
  faArrowDown,
  faArrowsUpDown
} from '@fortawesome/free-solid-svg-icons';

/**
 * ASCII Pattern Background Component for Sidebars
 */
const SidebarPatternBackground = ({ side = 'left' }) => {
  const [frame, setFrame] = useState(0);
  const [patternDirection, setPatternDirection] = useState(1); // 1 for forward, -1 for reverse
  
  const patternTypes = ['thermal', 'organic', 'irregular', 'blobs'];
  const width = 45;
  const height = 200;
  const slowdownFactor = 120; // Much slower (was 24, now 120 = 5x slower)
  const maxFrames = 2400; // Much longer cycle for ultra-smooth transitions
  
  const patterns = {
    thermal: (x, y, t) => {
      const cx1 = 2 + Math.sin(t * 0.3) * 2;
      const cy1 = 15 + Math.cos(t * 0.25) * 8;
      const cx2 = 28 + Math.cos(t * 0.4) * 2;
      const cy2 = 25 + Math.sin(t * 0.35) * 6;
      const cx3 = 15 + Math.sin(t * 0.5) * 8;
      const cy3 = 3 + Math.cos(t * 0.45) * 3;
      const cx4 = 18 + Math.cos(t * 0.6) * 6;
      const cy4 = 55 + Math.sin(t * 0.5) * 7;
      const cx5 = 1 + Math.sin(t * 0.7) * 1;
      const cy5 = 70 + Math.cos(t * 0.55) * 15;
      const cx6 = 20 + Math.cos(t * 0.8) * 8;
      const cy6 = 96 + Math.sin(t * 0.65) * 4;
      
      const dist1 = Math.sqrt((x - cx1) ** 2 + (y - cy1) ** 2);
      const dist2 = Math.sqrt((x - cx2) ** 2 + (y - cy2) ** 2);
      const dist3 = Math.sqrt((x - cx3) ** 2 + (y - cy3) ** 2);
      const dist4 = Math.sqrt((x - cx4) ** 2 + (y - cy4) ** 2);
      const dist5 = Math.sqrt((x - cx5) ** 2 + (y - cy5) ** 2);
      const dist6 = Math.sqrt((x - cx6) ** 2 + (y - cy6) ** 2);
      
      const heat1 = Math.exp(-dist1 * 0.15) * (0.6 + Math.sin(t * 0.6) * 0.2);
      const heat2 = Math.exp(-dist2 * 0.18) * (0.5 + Math.cos(t * 0.7) * 0.3);
      const heat3 = Math.exp(-dist3 * 0.2) * (0.4 + Math.sin(t * 0.8) * 0.15);
      const heat4 = Math.exp(-dist4 * 0.16) * (0.55 + Math.cos(t * 0.9) * 0.25);
      const heat5 = Math.exp(-dist5 * 0.19) * (0.45 + Math.sin(t * 1.0) * 0.2);
      const heat6 = Math.exp(-dist6 * 0.17) * (0.5 + Math.cos(t * 1.1) * 0.18);
      
      const interaction12 = Math.exp(-Math.abs(dist1 - dist2) * 0.1) * 0.1;
      const interaction34 = Math.exp(-Math.abs(dist3 - dist4) * 0.1) * 0.08;
      const interaction56 = Math.exp(-Math.abs(dist5 - dist6) * 0.1) * 0.09;
      
      return heat1 + heat2 + heat3 + heat4 + heat5 + heat6 + interaction12 + interaction34 + interaction56;
    },
    
    organic: (x, y, t) => {
      const cluster1 = Math.sin((x - 1) * 0.4 + t * 0.4) * Math.cos(y * 0.15 + t * 0.3) * 0.3;
      const cluster2 = Math.cos((x - 28) * 0.3 + t * 0.5) * Math.sin(y * 0.12 + t * 0.6) * 0.25;
      const cluster3 = Math.sin(x * 0.35 + t * 0.35) * Math.cos((y - 2) * 0.6 + t * 0.4) * 0.28;
      const cluster4 = Math.cos((x - 8) * 0.38 + t * 0.45) * Math.sin((y - 40) * 0.14 + t * 0.5) * 0.26;
      const cluster5 = Math.sin((x + 1) * 0.32 + t * 0.6) * Math.cos((y - 60) * 0.16 + t * 0.35) * 0.24;
      const cluster6 = Math.cos(x * 0.36 + t * 0.55) * Math.sin((y - 97) * 0.5 + t * 0.65) * 0.27;
      
      return cluster1 + cluster2 + cluster3 + cluster4 + cluster5 + cluster6;
    },
    
    irregular: (x, y, t) => {
      const source1 = Math.sin((x + 1) * 0.25 + y * 0.15 + t * 0.3) * Math.exp(-((x - 0) ** 2 + (y - 18) ** 2) * 0.003) * 0.4;
      const source2 = Math.cos((x - 28) * 0.2 + y * 0.18 - t * 0.25) * Math.exp(-((x - 29) ** 2 + (y - 32) ** 2) * 0.004) * 0.35;
      const source3 = Math.sin(x * 0.28 + (y + 1) * 0.4 + t * 0.4) * Math.exp(-((x - 11) ** 2 + (y - 1) ** 2) * 0.005) * 0.38;
      const source4 = Math.cos(x * 0.22 + y * 0.16 - t * 0.35) * Math.exp(-((x - 23) ** 2 + (y - 62) ** 2) * 0.005) * 0.33;
      const source5 = Math.sin((x - 1) * 0.26 + y * 0.14 + t * 0.45) * Math.exp(-((x - 2) ** 2 + (y - 77) ** 2) * 0.006) * 0.36;
      const source6 = Math.cos(x * 0.24 + (y - 97) * 0.5 - t * 0.5) * Math.exp(-((x - 20) ** 2 + (y - 98) ** 2) * 0.004) * 0.32;
      
      return source1 + source2 + source3 + source4 + source5 + source6;
    },
    
    blobs: (x, y, t) => {
      const blob1 = Math.exp(-((x - 0 + Math.sin(t * 0.3) * 3) ** 2 + (y - 12) ** 2) * 0.02) * 0.5;
      const blob2 = Math.exp(-((x - 29 + Math.cos(t * 0.4) * 1) ** 2 + (y - 20) ** 2) * 0.025) * 0.4;
      const blob3 = Math.exp(-((x - 12) ** 2 + (y - 1 + Math.cos(t * 0.3) * 2) ** 2) * 0.03) * 0.45;
      const blob4 = Math.exp(-((x - 25) ** 2 + (y - 45 + Math.sin(t * 0.6) * 10) ** 2) * 0.022) * 0.4;
      const blob5 = Math.exp(-((x - 2 + Math.cos(t * 0.7) * 2) ** 2 + (y - 60) ** 2) * 0.028) * 0.35;
      const blob6 = Math.exp(-((x - 18) ** 2 + (y - 75 + Math.sin(t * 0.4) * 6) ** 2) * 0.024) * 0.42;
      const blob7 = Math.exp(-((x - 8) ** 2 + (y - 98 + Math.sin(t * 0.8) * 2) ** 2) * 0.026) * 0.38;
      const blob8 = Math.exp(-((x - 28 + Math.cos(t * 0.5) * 2) ** 2 + (y - 85) ** 2) * 0.023) * 0.41;
      
      return blob1 + blob2 + blob3 + blob4 + blob5 + blob6 + blob7 + blob8;
    }
  };

  useEffect(() => {
    let animationId;
    
    const animate = () => {
      setFrame(prevFrame => {
        let nextFrame = prevFrame + patternDirection;
        
        // Boomerang logic - reverse direction at endpoints
        if (nextFrame >= maxFrames) {
          setPatternDirection(-1);
          nextFrame = maxFrames - 1;
        } else if (nextFrame <= 0) {
          setPatternDirection(1);
          nextFrame = 1;
        }
        
        return nextFrame;
      });
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [patternDirection, maxFrames]);

  // Remove auto-cycling - let boomerang handle pattern evolution

  const generateAsciiArt = () => {
    const t = (frame * Math.PI) / (60 * slowdownFactor);
    
    // Ultra-smooth pattern blending with longer transitions
    const patternCycle = (frame / maxFrames) * patternTypes.length * 0.5; // Slower cycling
    const currentPatternIndex = Math.floor(patternCycle) % patternTypes.length;
    const nextPatternIndex = (currentPatternIndex + 1) % patternTypes.length;
    
    // Smoother blending with easing
    const rawBlendFactor = patternCycle - Math.floor(patternCycle);
    const blendFactor = 0.5 * (1 - Math.cos(rawBlendFactor * Math.PI)); // Smooth easing
    
    const currentPattern = patterns[patternTypes[currentPatternIndex]];
    const nextPattern = patterns[patternTypes[nextPatternIndex]];
    
    let result = '';
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Ultra-smooth blending between patterns
        const value1 = currentPattern(x, y, t);
        const value2 = nextPattern(x, y, t);
        const blendedValue = value1 * (1 - blendFactor) + value2 * blendFactor;
        
        // Gentler thresholds for smoother transitions
        if (blendedValue > 0.3) {
          result += '█';
        } else if (blendedValue > 0.18) {
          result += '▓';
        } else if (blendedValue > 0.1) {
          result += '▒';
        } else if (blendedValue > 0.05) {
          result += '░';
        } else if (blendedValue > 0.015) {
          result += '·';
        } else {
          result += ' ';
        }
      }
      result += '\n';
    }
    
    return result;
  };

  return (
    <div style={{
      position: 'fixed',
      [side]: '0px',
      top: 0,
      width: '210px',
      height: '100vh',
      zIndex: 1,
      overflow: 'hidden'
    }}>
      <pre style={{
        fontFamily: 'monospace',
        fontSize: '8px',
        lineHeight: '0.8',
        letterSpacing: '0.05em',
        color: '#1E3A8A',
        userSelect: 'none',
        margin: 0,
        padding: 0,
        opacity: 1.0, // 2x as blue (was 0.7, now full opacity)
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {generateAsciiArt()}
      </pre>
    </div>
  );
};

/**
 * Signals Component with Enhanced Video Sidebars and Boomerang Effects
 */
const Signals = () => {
  // Router hooks for URL parameter handling
  const location = useLocation();
  const navigate = useNavigate();

  // Access user context for functionality
  const { 
    saveItem, 
    isSaved 
  } = useContext(UserContext);
  
  const [allSignals, setAllSignals] = useState([]);
  const [displaySignals, setDisplaySignals] = useState([]);
  const [visibleSignals, setVisibleSignals] = useState([]);
  const [featuredSignals, setFeaturedSignals] = useState([]);
  const [suggestedBrands, setSuggestedBrands] = useState([]);
  const [captions, setCaptions] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [currentFilter, setCurrentFilter] = useState('All'); // ONE FILTER FOR EVERYTHING
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoverStates, setHoverStates] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [screenSize, setScreenSize] = useState('large');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [sortOption, setSortOption] = useState('featured');
  const [scrollDirection, setScrollDirection] = useState('up');
  const [currentScrollY, setCurrentScrollY] = useState(0);
  const [showBottomNav, setShowBottomNav] = useState(false);
  const signalsPerPage = 24;
  const observer = useRef();
  
  // Video references for pattern backgrounds
  const leftPatternRef = useRef(null);
  const rightPatternRef = useRef(null);
  
  // Flag to prevent duplicate operations
  const loadingMoreRef = useRef(false);
  const prevScrollY = useRef(0);
  
  // Debug mode for troubleshooting
  const debug = false;

  // Pattern background cleanup
  useEffect(() => {
    return () => {
      // Cleanup handled by individual pattern components
    };
  }, []);

  // Handle scroll direction for bottom nav
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY > currentScrollY) {
        setScrollDirection('down');
        setShowBottomNav(false);
      } else {
        setScrollDirection('up');
        setShowBottomNav(scrollY > 100);
      }
      setCurrentScrollY(scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentScrollY]);

  // Handle responsive screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setScreenSize('small');
      } else if (width < 1050) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Window resize listener for control bar
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle card hover state
  const handleCardMouseEnter = useCallback((signalId) => {
    setHoverStates(prev => ({
      ...prev,
      [signalId]: true
    }));
  }, []);

  const handleCardMouseLeave = useCallback((signalId) => {
    setHoverStates(prev => ({
      ...prev,
      [signalId]: false
    }));
  }, []);

  // Toggle filters for mobile
  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  // Helper function to shuffle array
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  // Handle image load failure
  const handleImageError = useCallback((signalId, imageUrl) => {
    setFailedImages(prev => ({
      ...prev,
      [signalId]: true
    }));
    
    if (debug) {
      console.error(`Failed to load image: ${imageUrl}`);
    }
  }, [debug]);

  // Preload next batch of images for smoother transitions
  const preloadNextBatch = useCallback((signals, startIndex, count) => {
    const endIndex = Math.min(startIndex + count, signals.length);
    
    for (let i = startIndex; i < endIndex; i++) {
      const signal = signals[i];
      if (signal && signal.imageUrl) {
        const img = new Image();
        img.src = signal.imageUrl;
      }
    }
  }, []);

  // Get caption for a signal
  const getCaptionForSignal = useCallback((signal) => {
    if (!signal || !signal.imageUrl || !captions.length) return '';
    
    const filename = signal.imageUrl.split('/').pop();
    const captionEntry = captions.find(c => c.imageUrl.split('/').pop() === filename);
    return captionEntry?.caption || '';
  }, [captions]);

  // Get category for a signal based on brand or caption
  const getCategoryForSignal = useCallback((signal) => {
    const brand = (signal.brand || '').toLowerCase();
    const caption = getCaptionForSignal(signal).toLowerCase();
    
    // HIGHEST REVENUE POTENTIAL - Luxury goods, high-ticket items
    if (brand.includes('hermes') || brand.includes('hermès') || brand.includes('prada') || brand.includes('valextra') || 
        brand.includes('smythson') || brand.includes('jacques marie') || brand.includes('rolex') ||
        brand.includes('cartier') || brand.includes('tiffany') || brand.includes('louis vuitton') ||
        brand.includes('chanel') || brand.includes('gucci') || brand.includes('dior') ||
        caption.includes('luxury') || caption.includes('jewelry') || caption.includes('watch') ||
        caption.includes('handbag') || caption.includes('leather goods') || caption.includes('premium accessories')) {
      return 'Luxury';
    }
    
    // HIGH REVENUE - Books, Media, Publications
    if (brand.includes('taschen') || brand.includes('phaidon') || brand.includes('assouline') ||
        brand.includes('rizzoli') || brand.includes('aperture') || brand.includes('gestalten') ||
        brand.includes('thames hudson') || brand.includes('laurence king') || brand.includes('prestel') ||
        brand.includes('abrams') || brand.includes('monacelli') || brand.includes('powerhouse') ||
        brand.includes('032c') || brand.includes('pin-up') || brand.includes('sleeper') ||
        brand.includes('frieze') || brand.includes('wallpaper') || brand.includes('dezeen') ||
        caption.includes('book') || caption.includes('publication') || caption.includes('magazine') ||
        caption.includes('coffee table book') || caption.includes('art book') || caption.includes('design book') ||
        caption.includes('photography book') || caption.includes('architecture book') || caption.includes('monograph') ||
        caption.includes('catalog') || caption.includes('journal') || caption.includes('quarterly') ||
        caption.includes('editorial') || caption.includes('print media')) {
      return 'Books & Media';
    }
    
    // HIGH REVENUE - Furniture
    if (brand.includes('herman miller') || brand.includes('knoll') || brand.includes('vitra') ||
        brand.includes('cassina') || brand.includes('b&b italia') || brand.includes('poltrona frau') ||
        brand.includes('west elm') || brand.includes('restoration hardware') || brand.includes('cb2') ||
        brand.includes('design within reach') || brand.includes('hay') || brand.includes('muuto') ||
        caption.includes('furniture') || caption.includes('sofa') || caption.includes('chair') ||
        caption.includes('table') || caption.includes('desk') || caption.includes('cabinet') ||
        caption.includes('bed') || caption.includes('dining') || caption.includes('seating')) {
      return 'Furniture';
    }
    
    // HIGH REVENUE - Travel/hospitality
    if (brand.includes('aman') || brand.includes('villa lario') || brand.includes('oriental express') ||
        brand.includes('four seasons') || brand.includes('ritz carlton') || brand.includes('mandarin oriental') ||
        brand.includes('away') || brand.includes('rimowa') || brand.includes('globe trotter') ||
        brand.includes('tumi') || brand.includes('samsonite') ||
        caption.includes('hotel') || caption.includes('resort') || caption.includes('travel') ||
        caption.includes('destination') || caption.includes('luxury stay') || caption.includes('luggage') ||
        caption.includes('suitcase') || caption.includes('travel gear') || caption.includes('vacation')) {
      return 'Travel';
    }
    
    // MEDIUM-HIGH REVENUE - Home decor
    if (brand.includes('frette') || brand.includes('future perfect') || brand.includes('galerie half') ||
        brand.includes('rewire') || brand.includes('gohar world') || brand.includes('arclinea') ||
        brand.includes('artemide') || brand.includes('flos') || brand.includes('louis poulsen') ||
        brand.includes('le creuset') || brand.includes('staub') || brand.includes('williams sonoma') ||
        caption.includes('home') || caption.includes('interior') || caption.includes('decor') ||
        caption.includes('lighting') || caption.includes('kitchen') || caption.includes('cookware') ||
        caption.includes('bedding') || caption.includes('linens') || caption.includes('tableware')) {
      return 'Home';
    }
    
    // MEDIUM-HIGH REVENUE - Objects
    if (brand.includes('muji') || brand.includes('iittala') ||
        brand.includes('alessi') || brand.includes('georg jensen') || brand.includes('marimekko') ||
        caption.includes('objects') || caption.includes('vase') || caption.includes('sculpture') ||
        caption.includes('ceramic') || caption.includes('glass') || caption.includes('design object') ||
        caption.includes('collectible') || caption.includes('decorative') || caption.includes('handcrafted') ||
        caption.includes('artisan') || caption.includes('limited edition')) {
      return 'Objects';
    }
    
    // MEDIUM REVENUE - Technology
    if (brand.includes('apple') || brand.includes('sonos') || brand.includes('bang olufsen') ||
        brand.includes('bose') || brand.includes('dyson') || brand.includes('nest') ||
        brand.includes('samsung') || brand.includes('lg') || brand.includes('sony') ||
        caption.includes('technology') || caption.includes('electronics') || caption.includes('speaker') ||
        caption.includes('headphones') || caption.includes('smart home') || caption.includes('audio') ||
        caption.includes('wireless') || caption.includes('bluetooth') || caption.includes('device')) {
      return 'Technology';
    }
    
    // MEDIUM REVENUE - Art
    if (brand.includes('moma') || brand.includes('artsy') || brand.includes('saatchi') ||
        brand.includes('gagosian') || brand.includes('pace gallery') ||
        caption.includes('art') || caption.includes('print') || caption.includes('artwork') ||
        caption.includes('photography') || caption.includes('poster') ||
        caption.includes('gallery') || caption.includes('museum') || caption.includes('fine art') ||
        caption.includes('limited print') || caption.includes('signed print')) {
      return 'Art';
    }
    
    // MEDIUM REVENUE - Fashion
    if (brand.includes('barbour') || brand.includes('arcteryx') || brand.includes('patagonia') ||
        brand.includes('jcrew') || brand.includes('everlane') || brand.includes('cos') ||
        brand.includes('uniqlo') || brand.includes('norse projects') || brand.includes('acne') ||
        brand.includes('oliver peoples') || brand.includes('james perse') ||
        caption.includes('fashion') || caption.includes('clothing') || caption.includes('apparel') ||
        caption.includes('shirt') || caption.includes('jacket') || caption.includes('dress') ||
        caption.includes('pants') || caption.includes('shoes') || caption.includes('accessories')) {
      return 'Fashion';
    }
    
    // MEDIUM REVENUE - Outdoor
    if (brand.includes('mystery ranch') || brand.includes('vollebak') || brand.includes('iceco') ||
        brand.includes('pelican') || brand.includes('snow peak') || brand.includes('helinox') ||
        brand.includes('rei') || brand.includes('north face') ||
        caption.includes('outdoor') || caption.includes('camping') || caption.includes('hiking') ||
        caption.includes('adventure') || caption.includes('gear') || caption.includes('backpack') ||
        caption.includes('tent') || caption.includes('sleeping bag')) {
      return 'Outdoor';
    }
    
    // LOWER REVENUE - Sports
    if (brand.includes('titleist') || brand.includes('mizuno') || brand.includes('scotty cameron') ||
        caption.includes('golf') || caption.includes('tennis') || caption.includes('ski') ||
        caption.includes('surf') || caption.includes('sport') || caption.includes('fitness') ||
        caption.includes('exercise') || caption.includes('athletic')) {
      return 'Sport';
    }
    
    // LOWER REVENUE - Garden
    if (brand.includes('niwaki') || caption.includes('garden') || caption.includes('plant') ||
        caption.includes('outdoor space') || caption.includes('landscaping') || caption.includes('tools') ||
        caption.includes('planting') || caption.includes('gardening')) {
      return 'Garden';
    }
    
    return 'General';
  }, [getCaptionForSignal]);

  // UNIFIED FILTER HANDLER - Works for BOTH lists
  const handleFilterChange = useCallback((filterValue) => {
    setCurrentFilter(filterValue);
    
    // Update URL
    const newUrl = new URL(window.location);
    if (filterValue !== 'All') {
      newUrl.searchParams.set('q', filterValue);
    } else {
      newUrl.searchParams.delete('q');
    }
    navigate(newUrl.pathname + newUrl.search, { replace: true });
  }, [navigate]);

  // Listen for header search events
  useEffect(() => {
    const handleHeaderSearch = (event) => {
      const searchTerm = event.detail?.searchTerm || '';
      handleFilterChange(searchTerm || 'All');
    };

    window.addEventListener('artifex-search', handleHeaderSearch);
    
    return () => {
      window.removeEventListener('artifex-search', handleHeaderSearch);
    };
  }, [handleFilterChange]);

  // Get search query from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('q');
    if (searchParam) {
      setCurrentFilter(searchParam);
    } else {
      setCurrentFilter('All');
    }
  }, [location.search]);

  // Filter signals based on current filter
  const filterSignals = useCallback((signals) => {
    if (currentFilter === 'All') {
      return signals;
    }
    
    const lowercaseFilter = currentFilter.toLowerCase();
    
    return signals.filter(signal => {
      const brand = (signal.brand || '').toLowerCase();
      const caption = getCaptionForSignal(signal) || '';
      const category = getCategoryForSignal(signal).toLowerCase();
      
      return (
        brand.includes(lowercaseFilter) || 
        caption.toLowerCase().includes(lowercaseFilter) ||
        category.includes(lowercaseFilter) ||
        category === currentFilter.toLowerCase()
      );
    });
  }, [currentFilter, getCaptionForSignal, getCategoryForSignal]);

  // Add more signals to visible set
  const addMoreSignalsFn = useCallback(() => {
    if (loadingMoreRef.current) return;
    if (visibleSignals.length >= displaySignals.length) {
      if (displaySignals.length > 0) {
        const nextBatch = displaySignals.slice(0, signalsPerPage);
        setVisibleSignals(prev => [...prev, ...nextBatch]);
        
        setTimeout(() => {
          preloadNextBatch(displaySignals, 0, signalsPerPage * 2);
        }, 100);
      }
      return;
    }
    
    loadingMoreRef.current = true;
    
    setTimeout(() => {
      const currentCount = visibleSignals.length;
      const nextBatchIndex = currentCount % displaySignals.length;
      const remainingSignals = displaySignals.length - nextBatchIndex;
      const signalsToAdd = Math.min(signalsPerPage, remainingSignals);
      
      const nextBatch = displaySignals.slice(nextBatchIndex, nextBatchIndex + signalsToAdd);
      
      let wrappedSignals = [];
      if (signalsToAdd < signalsPerPage && displaySignals.length > 0) {
        const neededFromStart = signalsPerPage - signalsToAdd;
        wrappedSignals = displaySignals.slice(0, neededFromStart);
      }
      
      setVisibleSignals(prev => [...prev, ...nextBatch, ...wrappedSignals]);
      
      const nextPreloadIndex = (nextBatchIndex + signalsToAdd) % displaySignals.length;
      setTimeout(() => {
        preloadNextBatch(displaySignals, nextPreloadIndex, signalsPerPage * 2);
      }, 100);
      
      loadingMoreRef.current = false;
    }, 100);
  }, [displaySignals, visibleSignals, signalsPerPage, preloadNextBatch]);

  const [shouldLoadMore, setShouldLoadMore] = useState(false);

  useEffect(() => {
    if (shouldLoadMore) {
      addMoreSignalsFn();
      setShouldLoadMore(false);
    }
  }, [shouldLoadMore, addMoreSignalsFn]);

  // Infinite scroll implementation
  const lastSignalRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && displaySignals.length > visibleSignals.length) {
        setShouldLoadMore(true);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [displaySignals, visibleSignals]);

  // Add scroll handler for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY <= prevScrollY.current) {
        prevScrollY.current = currentScrollY;
        return;
      }
      
      prevScrollY.current = currentScrollY;
      
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollBottom = documentHeight - (currentScrollY + windowHeight);
      
      const triggerDistance = windowHeight * 0.3;
      
      if (scrollBottom < triggerDistance && !loadingMoreRef.current) {
        setShouldLoadMore(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Fetch and load all signal content
  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true);
      try {
        document.body.style.overflow = 'auto';
        
        const adsRes = await fetch('/ads.json');
        const captionsRes = await fetch('/assets-signals/signals_captions.json');
        
        if (!adsRes.ok || !captionsRes.ok) {
          throw new Error('Failed to fetch signals data');
        }
        
        const adsData = await adsRes.json();
        const captionsData = await captionsRes.json();
        
        setCaptions(captionsData);
        
        const processedSignals = adsData.map((signal, index) => ({
          ...signal,
          id: `signal-${index}`,
        }));
        
        const shuffledSignals = shuffleArray(processedSignals);
        setAllSignals(shuffledSignals);
        
        // Featured signals - SHUFFLE HERE
        const featuredList = [...shuffledSignals].sort(() => 0.5 - Math.random()).slice(0, 30);
        setFeaturedSignals(featuredList);
        
        const createSuggestedBrands = () => {
          return shuffleArray(shuffledSignals).slice(0, 5);
        };
        
        setSuggestedBrands(createSuggestedBrands());
        setIsLoading(false);
      } catch (err) {
        console.error('❌ Error loading signals content:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };
    
    fetchContent();
  }, [shuffleArray]);

  // Build categories when signals are loaded
  useEffect(() => {
    const allPossibleCategories = [
      'All',
      'Luxury',
      'Books & Media',
      'Furniture', 
      'Travel',
      'Home',
      'Objects',
      'Technology',
      'Art',
      'Fashion',
      'Outdoor',
      'Sport',
      'Garden',
      'General'
    ];
    
    if (allSignals.length > 0) {
      const existingCategorySet = new Set();
      allSignals.forEach(signal => {
        const category = getCategoryForSignal(signal);
        if (category) {
          existingCategorySet.add(category);
        }
      });
      
      const categoriesWithContent = allPossibleCategories.filter(cat => 
        cat === 'All' || existingCategorySet.has(cat)
      );
      
      setCategories(categoriesWithContent);
    } else {
      setCategories(allPossibleCategories);
    }
  }, [allSignals, getCategoryForSignal]);

  // Update display signals when filter changes
  useEffect(() => {
    if (!allSignals.length) return;
    
    loadingMoreRef.current = false;
    
    const filteredSignals = filterSignals(allSignals.filter(signal => !failedImages[signal.id]));
    setDisplaySignals(filteredSignals);
    setVisibleSignals(filteredSignals.slice(0, signalsPerPage));
    
    setTimeout(() => {
      preloadNextBatch(filteredSignals, signalsPerPage, signalsPerPage);
    }, 500);
  }, [currentFilter, allSignals, failedImages, signalsPerPage, filterSignals, preloadNextBatch]);
  
  // Handle save action
  const handleSave = useCallback((e, signal) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (saveItem) {
      saveItem(signal.id);
      
      if (debug) console.log('Saved signal:', signal.brand || signal.id);
    }
  }, [saveItem, debug]);

  // Handle view action
  const handleView = useCallback((e, sourceUrl) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(sourceUrl, '_blank');
  }, []);

  if (isLoading && !visibleSignals.length) {
    return (
      <div className="signals-loading" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '34vh',
        gap: '1rem'
      }}>
        <div className="loading-spinner" style={{
          width: '27.2px',
          height: '27.2px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #0d47a1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Loading signals feed...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="signals-error" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '34vh',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h2>Error loading content</h2>
        <p style={{ color: '#666' }}>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#0d47a1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Top navigation menu - show only above 576px width */}
      {windowWidth >= 576 && (
        <div style={{ 
          marginTop: '60px',
          width: '100%', 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'white',
          borderBottom: '1px solid #eee',
          padding: '0.5rem 0'
        }}>
          <div style={{ 
           maxWidth: '750px',
           width: '100%',
           display: 'flex',
           justifyContent: 'space-between',
           alignItems: 'center'
         }}>
           <Link
             to="/discover"
             style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               flexDirection: 'column',
               padding: '0.5rem 1rem',
               background: 'transparent',
               color: '#666',
               textDecoration: 'none',
               borderRadius: '6px',
               minWidth: '80px',
               whiteSpace: 'nowrap',
               overflow: 'hidden'
             }}
           >
             <FontAwesomeIcon
               icon={faHome}
               style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
             />
             <span style={{ fontSize: '0.8rem' }}>Discover</span>
           </Link>

        <Link
          to="/signals"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '0.5rem 1rem',
            background: '#0d47a1',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            minWidth: '80px',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}
        >
          <FontAwesomeIcon
            icon={faShoppingBag}
            style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
          />
          <span style={{ fontSize: '0.8rem' }}>Signals</span>
        </Link>

        <Link
          to="/editorials"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '0.5rem 1rem',
            background: 'transparent',
            color: '#666',
            textDecoration: 'none',
            borderRadius: '6px',
            minWidth: '80px',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}
        >
          <FontAwesomeIcon
            icon={faNewspaper}
            style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
          />
          <span style={{ fontSize: '0.8rem' }}>Editorials</span>
        </Link>
      </div>
    </div>
  )}
  
  {/* Mobile filters drawer */}
  {windowWidth < 1300 && showFilters && (
    <div style={{
      position: 'fixed',
      top: '60px',
      left: 0,
      right: 0,
      background: 'white',
      zIndex: 900,
      padding: '1rem',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s ease',
      transform: showFilters ? 'translateY(0)' : 'translateY(-100%)'
    }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Categories</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        {categories.map((category, index) => (
          <button 
            key={index}
            onClick={() => {
              handleFilterChange(category);
              setShowFilters(false);
            }}
            style={{
              padding: '0.5rem 1rem',
              background: currentFilter === category ? '#0d47a1' : '#f5f5f5',
              color: currentFilter === category ? 'white' : '#333',
              border: 'none',
              borderRadius: '20px',
              fontSize: '0.8rem',
              cursor: 'pointer'
            }}
          >
            {category}
          </button>
        ))}
      </div>
      
      <button 
        onClick={() => setShowFilters(false)}
        style={{
          display: 'block',
          width: '100%',
          padding: '0.5rem',
          background: '#f5f5f5',
         border: 'none',
         borderRadius: '4px',
         fontSize: '0.9rem',
         cursor: 'pointer'
       }}
     >
       Close Filters
     </button>
   </div>
 )}
 
 {/* Add spacing when menu is hidden */}
 {windowWidth < 576 && <div style={{ marginTop: '60px' }}></div>}
 
 {/* Main content container */}
 <div className="signals-container" style={{ 
   paddingTop: windowWidth >= 576 ? '20px' : '20px'
 }}>
   {/* Left categories sidebar - matching enhanced layout */}
   {windowWidth >= 1300 && (
     <>
       {/* Left Sidebar Pattern Background */}
       <SidebarPatternBackground side="left" />
       
       {/* Left Sidebar Content */}
       <div className="signals-left-sidebar">
         <div className="sidebar-section" style={{
           background: 'rgba(255, 255, 255, 0.3)',
           borderRadius: '8px',
           padding: '0.2rem 1rem 1rem',
           marginLeft: '10px',
           marginRight: '10px',
           marginTop: '10px',
           marginBottom: '20px',
           minHeight: '650px'
         }}>
           <h3>Categories</h3>
           <div style={{ 
             display: 'flex', 
             flexDirection: 'column', 
             gap: '0.5rem',
             maxHeight: '585px',
             overflowY: 'auto',
             marginTop: '.25rem',
             marginBottom: '1rem'
           }}>
             {categories.map((category, index) => (
               <button 
                 key={index}
                 onClick={() => handleFilterChange(category)}
                 className={currentFilter === category ? 'selected' : ''}
               >
                 {category}
               </button>
             ))}
           </div>

           <div style={{
             fontSize: '11px',
             color: '#666',
             fontStyle: 'italic',
             textAlign: 'center',
             padding: '0.5rem',
             marginTop: '0.5rem'
           }}>
             Scroll for more
           </div>
         </div>
       </div>
     </>
   )}

   {/* Fixed Bottom Navigation - Visible on scroll up */}
   <div style={{
     position: 'fixed',
     bottom: showBottomNav ? '20px' : '-80px',
     left: '50%',
     transform: 'translateX(-50%)',
     display: 'flex',
     justifyContent: 'center',
     alignItems: 'center',
     gap: '1rem',
     padding: '12px 24px',
     background: 'rgba(255, 255, 255, 0.25)',
     backdropFilter: 'blur(20px) saturate(180%)',
     WebkitBackdropFilter: 'blur(20px) saturate(180%)',
     borderRadius: '50px',
     border: '1px solid rgba(255, 255, 255, 0.4)',
     boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 2px 4px rgba(255, 255, 255, 0.5)',
     transition: 'bottom 0.3s ease',
     zIndex: 1000
   }}>
       
         <Link
           to="/saved"
           title="Saved items"
           style={{
             padding: 0,
             background: 'rgba(255, 255, 255, 0.2)',
             backdropFilter: 'blur(20px) saturate(200%)',
             WebkitBackdropFilter: 'blur(20px) saturate(200%)',
             color: '#666',
             border: '1px solid rgba(255, 255, 255, 0.5)',
             borderRadius: '50%',
             cursor: 'pointer',
             textDecoration: 'none',
             display: 'inline-flex',
             alignItems: 'center',
             justifyContent: 'center',
             width: '40px',
             height: '40px',
             transition: 'all 0.3s ease',
             boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
           }}
           onMouseEnter={(e) => {
             if (windowWidth >= 576) {
                
               e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
               e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
             }
           }}
           onMouseLeave={(e) => {
              
             e.currentTarget.style.transform = 'translateY(0)';
             e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
           }}
         >
           <FontAwesomeIcon icon={faHeart} style={{ fontSize: '16px' }} />
         </Link>

         {windowWidth < 1300 && (
           <button
             onClick={toggleFilters}
             title="Filters"
             style={{
               padding: 0,
               background: 'rgba(255, 255, 255, 0.2)',
               backdropFilter: 'blur(20px) saturate(200%)',
               WebkitBackdropFilter: 'blur(20px) saturate(200%)',
               color: '#666',
               border: '1px solid rgba(255, 255, 255, 0.5)',
               borderRadius: '50%',
               cursor: 'pointer',
               display: 'inline-flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '40px',
               height: '40px',
               transition: 'all 0.3s ease',
               boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
             }}
             onMouseEnter={(e) => {
               if (windowWidth >= 576) {
                  
                 e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                 e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
               }
             }}
             onMouseLeave={(e) => {
                
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
             }}
           >
             <FontAwesomeIcon icon={faFilter} style={{ fontSize: '16px' }} />
           </button>
         )}

         <div style={{ position: 'relative', display: 'inline-block' }}>
           <button
             title={`Sort: ${sortOption === 'featured' ? 'Featured' : sortOption === 'newest' ? 'Newest' : 'Most Popular'}`}
             style={{
               padding: 0,
               background: 'rgba(255, 255, 255, 0.2)',
               backdropFilter: 'blur(20px) saturate(200%)',
               WebkitBackdropFilter: 'blur(20px) saturate(200%)',
               color: '#666',
               border: '1px solid rgba(255, 255, 255, 0.5)',
               borderRadius: '50%',
               cursor: 'pointer',
               display: 'inline-flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '40px',
               height: '40px',
               transition: 'all 0.3s ease',
               boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
             }}
             onClick={() => {
               const select = document.getElementById('signals-sort-select');
               if (select) select.focus();
             }}
             onMouseEnter={(e) => {
               if (windowWidth >= 576) {
                  
                 e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                 e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
               }
             }}
             onMouseLeave={(e) => {
                
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
             }}
           >
             <FontAwesomeIcon icon={faArrowsUpDown} style={{ fontSize: '16px' }} />
           </button>
           <select
             id="signals-sort-select"
             value={sortOption}
             onChange={(e) => setSortOption(e.target.value)}
             style={{
               position: 'absolute',
               top: 0,
               left: 0,
               width: '40px',
               height: '40px',
               opacity: 0,
               cursor: 'pointer',
               borderRadius: '50%'
             }}
           >
             <option value="featured">Featured</option>
             <option value="newest">Newest</option>
             <option value="popular">Most Popular</option>
           </select>
         </div>
   </div>

   {/* Main grid container */}
   <div className="signals-main">
     {/* Empty state */}
     {visibleSignals.length === 0 && !isLoading && (
       <div style={{
         padding: '3rem 1rem',
         textAlign: 'center',
         background: '#f9f9f9',
         borderRadius: '8px'
       }}>
         <FontAwesomeIcon 
           icon={faShoppingBag} 
           style={{ fontSize: '2.5rem', color: '#ccc', marginBottom: '1rem' }}
         />
         <h3 style={{ margin: '0 0 0.5rem', color: '#555' }}>No signals found</h3>
         <p style={{ margin: '0 0 1rem', color: '#777' }}>
           {currentFilter !== 'All' ? `No signals found for "${currentFilter}". Try a different filter.` : 'Try adjusting your filters.'}
         </p>
         <button
           onClick={() => handleFilterChange('All')}
           style={{
             padding: '0.5rem 1rem',
             background: '#0d47a1',
             color: 'white',
             border: 'none',
             borderRadius: '4px',
             cursor: 'pointer'
           }}
         >
           Reset Filters
         </button>
       </div>
     )}

     {/* Responsive grid layout */}
     <div className="signals-grid">
       {visibleSignals.map((signal, index) => (
         <div 
           key={`${signal.id}-${index}`} 
           className="signals-card fade-in"
           onMouseEnter={() => handleCardMouseEnter(signal.id)}
           onMouseLeave={() => handleCardMouseLeave(signal.id)}
           ref={index === visibleSignals.length - 1 ? lastSignalRef : null}
         >
           <a href={signal.sourceUrl} target="_blank" rel="noopener noreferrer" className="card-link" onClick={(e) => e.preventDefault()}>
             <div className="card-image-container" style={{
               width: '100%',
               height: '350px',
               position: 'relative',
               overflow: 'hidden'
             }}>
               <img 
                 src={signal.imageUrl}
                 alt={signal.brand || 'Signal content'} 
                 className="card-image"
                 style={{
                   width: '100%',
                   height: '350px',
                   objectFit: 'cover',
                   objectPosition: 'center',
                   border: 'none',
                   margin: 0,
                   padding: 0,
                   display: 'block'
                 }}
                 onError={() => handleImageError(signal.id, signal.imageUrl)}
               />
             </div>
             
             {/* Action buttons - transparent and only visible on hover */}
             <div
               style={{
                 position: 'absolute',
                 top: '10px',
                 right: '10px',
                 display: 'flex',
                 flexDirection: 'column',
                 gap: windowWidth < 430 ? '6px' : '8px',
                 opacity: hoverStates[signal.id] ? 1 : 0,
                 transition: 'opacity 0.2s ease'
               }}
             >
               <button
                 onClick={(e) => handleSave(e, signal)}
                 style={{
                   width: windowWidth < 430 ? '20px' : windowWidth < 768 ? '24px' : '28px',
                   height: windowWidth < 430 ? '20px' : windowWidth < 768 ? '24px' : '28px',
                   borderRadius: '50%',
                   background: 'rgba(255, 255, 255, 0.8)',
                   border: 'none',
                   display: 'flex',
                   justifyContent: 'center',
                   alignItems: 'center',
                   cursor: 'pointer',
                   boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                 }}
                 title="Save"
               >
                 <FontAwesomeIcon
                   icon={faBookmark}
                   style={{
                     color: isSaved ? (isSaved(signal.id) ? '#000080' : '#666') : '#666',
                     fontSize: windowWidth < 430 ? '10px' : windowWidth < 768 ? '12px' : '14px'
                   }}
                 />
               </button>

               <button
                 onClick={(e) => handleView(e, signal.sourceUrl)}
                 style={{
                   width: windowWidth < 430 ? '20px' : windowWidth < 768 ? '24px' : '28px',
                   height: windowWidth < 430 ? '20px' : windowWidth < 768 ? '24px' : '28px',
                   borderRadius: '50%',
                   background: 'rgba(255, 255, 255, 0.8)',
                   border: 'none',
                   display: 'flex',
                   justifyContent: 'center',
                   alignItems: 'center',
                   cursor: 'pointer',
                   boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                 }}
                 title="Share"
               >
                 <FontAwesomeIcon
                   icon={faPaperPlane}
                   style={{
                     color: '#666',
                     fontSize: windowWidth < 430 ? '10px' : windowWidth < 768 ? '12px' : '14px'
                   }}
                 />
               </button>
             </div>
             
             {/* Hover overlay with info - slightly different from Discover with blue tint */}
             {windowWidth >= 768 && (
             <div
               style={{
                 position: 'absolute',
                 bottom: 0,
                 left: 0,
                 right: 0,
                 background: 'linear-gradient(to top, rgba(240, 245, 255, 0.85) 0%, rgba(240, 245, 255, 0.8) 70%, rgba(240, 245, 255, 0.6) 80%, rgba(240, 245, 255, 0.45) 85%, rgba(240, 245, 255, 0.25) 90%, rgba(240, 245, 255, 0.1) 95%, rgba(240, 245, 255, 0) 100%)',
                 padding: '0.8rem 0.8rem 0.6rem 0.8rem',
                 opacity: hoverStates[signal.id] ? 1 : 0,
                 transform: hoverStates[signal.id] ? 'translateY(0)' : 'translateY(100%)',
                 transition: 'all 0.3s ease',
                 height: '180px',
                 overflow: 'hidden',
                 zIndex: 10,
                 display: 'flex',
                 flexDirection: 'column'
               }}
             >
               <div style={{
                 flex: 1,
                 display: 'flex',
                 flexDirection: 'column',
                 paddingTop: '2rem',
                 paddingBottom: '0.3rem'
               }}>
                 <h3 style={{
                   margin: '0 0 0.4rem 0',
                   fontSize: '0.7rem',
                   fontWeight: 600,
                   color: '#1a4380',
                   display: '-webkit-box',
                   WebkitLineClamp: 2,
                   WebkitBoxOrient: 'vertical',
                   overflow: 'hidden',
                   lineHeight: '1.3',
                   wordBreak: 'break-word'
                 }}>
                   {signal.brand || 'Untitled'}
                 </h3>

                 {getCaptionForSignal(signal) && (
                 <p style={{
                   fontSize: '0.6rem',
                   color: '#555',
                   margin: '0 0 0.5rem 0',
                   lineHeight: '1.4',
                   wordBreak: 'break-word',
                   fontStyle: 'italic',
                   display: '-webkit-box',
                   WebkitLineClamp: 3,
                   WebkitBoxOrient: 'vertical',
                   overflow: 'hidden'
                 }}>
                   {getCaptionForSignal(signal)}
                 </p>
                 )}

                 {/* Category tag */}
                 <div style={{
                   display: 'flex',
                   gap: '0.25rem',
                   marginBottom: '0.5rem'
                 }}>
                   <span style={{
                     background: '#0d47a1',
                     color: 'white',
                     padding: '3px 8px',
                     borderRadius: '12px',
                     fontSize: '0.55rem',
                     fontWeight: 500,
                     letterSpacing: '0.3px'
                   }}>
                     {getCategoryForSignal(signal)}
                   </span>
                 </div>
               </div>
             </div>
             )}
           </a>
         </div>
       ))}
     </div>
   </div>
   
   {/* Right sidebar - matching enhanced layout */}
   {windowWidth >= 1300 && (
     <>
       {/* Right Sidebar Pattern Background */}
       <SidebarPatternBackground side="right" />

       {/* Right Sidebar Content */}
       <div className="signals-right-sidebar">
         {/* Recommended section */}
         <div className="sidebar-section" style={{
           background: 'rgba(255, 255, 255, 0.3)',
           borderRadius: '8px',
           padding: '1rem',
           marginLeft: '10px',
           marginRight: '10px',
           marginTop: '10px',
           marginBottom: '1rem',
           paddingBottom: '1rem',
           minHeight: '530px'
         }}>
           <div className="section-header">
             <h3>Recommended</h3>
           </div>
           
           <div style={{ 
             display: 'flex', 
             flexDirection: 'column', 
             gap: '.2rem',
             maxHeight: '330px',
             overflowY: 'auto',
             marginTop: '-0rem',
             marginBottom: '1rem'
           }}>
             {[
               'Porsche',
               'Valextra', 
               'Worldofinteriors',
               '032c',
               'Gagosian',
               '1stdibs',
               'Hermès',
               'Taschen',
               'Aman',
               'B&B Italia',
               'Flos',
               'Louis Poulsen',
               'Sonos',
               'MOMA',
               'Barbour',
               'Mystery Ranch'
             ].map((brand, index) => (
               <button 
                 key={index}
                 onClick={() => handleFilterChange(brand)}
               >
                 {brand}
               </button>
             ))}
           </div>

           <div style={{
             fontSize: '11px',
             color: '#666',
             fontStyle: 'italic',
             textAlign: 'center',
             padding: '0.5rem',
             marginTop: '0.5rem'
           }}>
             Scroll for more
           </div>
         </div>
         
         {/* Footer */}
         <div className="sidebar-footer" style={{
           background: 'rgba(255, 255, 255, 0.3)',
           borderRadius: '8px',
           padding: '1rem',
           marginLeft: '10px',
           marginRight: '10px',
           marginBottom: '20px'
         }}>
           <div className="footer-links" style={{
             transform: 'translateX(20px)'
           }}>
             <span>About</span>
             <span>Help</span>
             <span>Terms</span>
             <span>Privacy</span>
           </div>
           <div className="copyright" style={{
             transform: 'translateX(15px)'
           }}>
             © Artifex {new Date().getFullYear()}
           </div>
           <div style={{
             fontSize: '8px',
             color: '#bbb',
             marginTop: '0.3rem',
             textAlign: 'center',
             opacity: 0.6
           }}>
             Editorial & sponsored content
           </div>
         </div>
       </div>
     </>
   )}
 </div>
</>
);
};

export default Signals;